import { useContext, type CSSProperties, type ReactNode } from 'react'
import { ScreenStateContext } from '@/components/console/screen-state'
import type { DataState } from '@/lib/demo/demo-context'
import { cn } from '@/lib/utils'

/**
 * Sets the data state for everything inside it, so each `<StateWrap>` below can
 * say when it shows without being handed the state. Get the state from
 * `useDataState()` (lib/demo/use-data-state.ts).
 */
export function ScreenState({ state, children }: { state: DataState; children: ReactNode }) {
  return <ScreenStateContext.Provider value={state}>{children}</ScreenStateContext.Provider>
}

interface StateWrapProps {
  /**
   * The states it shows in, space-separated like the prototype's `data-when`
   * ("populated partial offline"), or a list.
   */
  when: string | DataState[]
  /** Overrides the nearest `<ScreenState>`. */
  state?: DataState
  /** Extra classes: the prototype often puts `card ccol-3 stat` on the wrap itself. */
  className?: string
  style?: CSSProperties
  id?: string
  children: ReactNode
}

/**
 * The prototype's `.statewrap[data-when]` pattern: renders its children only in the
 * listed states, and nothing at all otherwise. Pair one per treatment: populated,
 * first-run empty, loading skeleton, partial notice, error alert, offline alert.
 */
export function StateWrap({ when, state, className, style, id, children }: StateWrapProps) {
  const current = useContext(ScreenStateContext)
  const list = typeof when === 'string' ? (when.split(/\s+/).filter(Boolean) as DataState[]) : when
  if (!list.includes(state ?? current)) return null
  return (
    <div className={cn('statewrap', className)} data-when={list.join(' ')} style={style} id={id}>
      {children}
    </div>
  )
}
